'use client';

import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import type { DateRange as DayPickerRange } from 'react-day-picker';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';

import { Button } from '@r/components/ui/button.tsx';
import { Calendar } from '@r/components/ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';

import { cn } from '@r/utils/utils.ts';
import { useTranslation } from 'react-i18next';
import { fr as frLocale } from 'date-fns/locale';
import { toast } from 'sonner';

type DateRange = DayPickerRange;

export interface DateRangePickerProps {
  onUpdate?: (values: { range: DateRange }) => void;
  initialDateFrom?: Date | string;
  initialDateTo?: Date | string;
  align?: 'start' | 'center' | 'end';
  locale?: string;
  isActive?: boolean;
  placeholder?: string;
  triggerClassName?: string;
  /** Rend le contenu (presets + calendrier + actions) directement, sans bouton/popover. */
  inline?: boolean;
  /** Appelé après Appliquer / Retour / Réinitialisation — utile en mode `inline` pour fermer le parent. */
  onClose?: () => void;
}

const formatDate = (date: Date, locale = 'fr-FR') => {
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === 'string') {
    const dateParts = dateInput.split('-').map((part) => Number.parseInt(part, 10));
    return new Date(dateParts[0], (dateParts[1] ?? 1) - 1, dateParts[2] ?? 1);
  }
  return dateInput;
};

interface Preset {
  name: string;
  label: string;
}

const PRESETS: Preset[] = [
  { name: 'today', label: 'common.today' },
  { name: 'yesterday', label: 'common.yesterday' },
  { name: 'last7', label: 'common.last7days' },
  { name: 'last14', label: 'common.last14days' },
  { name: 'last30', label: 'common.last30days' },
  { name: 'last12months', label: 'common.last12months' },
  { name: 'last24months', label: 'common.last24months' },
  { name: 'thisWeek', label: 'common.thisweek' },
  { name: 'lastWeek', label: 'common.lastweek' },
  { name: 'thisMonth', label: 'common.thismonth' },
  { name: 'lastMonth', label: 'common.lastmonth' }
];

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, days: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
const startOfWeek = (date: Date): Date => {
  const dayOfWeek = date.getDay();
  const mondayOffset = (dayOfWeek + 6) % 7;
  return startOfDay(addDays(date, -mondayOffset));
};
const endOfWeek = (date: Date): Date => addDays(startOfWeek(date), 6);
const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (date: Date, months: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
const monthSpan = (from: Date, to: Date): number =>
  to.getFullYear() * 12 + to.getMonth() - (from.getFullYear() * 12 + from.getMonth());

export const DateRangePicker: FC<DateRangePickerProps> = ({
  initialDateFrom = startOfDay(new Date()),
  initialDateTo,
  onUpdate,
  align = 'center',
  locale = 'fr-FR',
  isActive = true,
  placeholder,
  triggerClassName,
  inline = false,
  onClose
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const [range, setRange] = useState<DateRange>(() => {
    const from = startOfDay(getDateAdjustedForTimezone(initialDateFrom));
    const to = initialDateTo ? startOfDay(getDateAdjustedForTimezone(initialDateTo)) : from;
    return { from, to };
  });

  const openedRangeRef = useRef<DateRange | undefined>(undefined);
  const closeActionRef = useRef<'cancel' | 'apply' | 'reset' | undefined>(undefined);

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(undefined);
  const [, setValidationErrorKey] = useState<string | undefined>(undefined);

  const getPresetRange = useMemo(() => {
    return (presetName: string): DateRange => {
      const today = startOfDay(new Date());
      switch (presetName) {
        case 'today':
          return { from: today, to: today };
        case 'yesterday': {
          const yesterdayDate = addDays(today, -1);
          return { from: yesterdayDate, to: yesterdayDate };
        }
        case 'last7': {
          const from = addDays(today, -6);
          return { from, to: today };
        }
        case 'last14': {
          const from = addDays(today, -13);
          return { from, to: today };
        }
        case 'last30': {
          const from = addDays(today, -29);
          return { from, to: today };
        }
        case 'last12months': {
          const from = startOfDay(addMonths(today, -11));
          return { from, to: today };
        }
        case 'last24months': {
          const from = startOfDay(addMonths(today, -23));
          return { from, to: today };
        }
        case 'thisWeek': {
          const from = startOfWeek(today);
          const to = endOfWeek(today);
          return { from, to };
        }
        case 'lastWeek': {
          const previousWeekEnd = addDays(startOfWeek(today), -1);
          const previousWeekStart = startOfWeek(previousWeekEnd);
          return { from: previousWeekStart, to: previousWeekEnd };
        }
        case 'thisMonth': {
          const from = startOfMonth(today);
          const to = endOfMonth(today);
          return { from, to };
        }
        case 'lastMonth': {
          const ref = addDays(startOfMonth(today), -1);
          const from = startOfMonth(ref);
          const to = endOfMonth(ref);
          return { from, to };
        }
        default:
          return { from: today, to: today };
      }
    };
  }, []);

  const setPreset = (preset: string): void => {
    setSelectedPreset(preset);
    const computedRange = getPresetRange(preset);
    setRange(computedRange);
  };

  const areRangesEqual = (firstRange?: DateRange, secondRange?: DateRange): boolean => {
    if (!firstRange && !secondRange) return true;
    if (!firstRange || !secondRange) return false;
    const firstFromTimestamp = firstRange.from?.getTime();
    const secondFromTimestamp = secondRange.from?.getTime();
    const firstToTimestamp = firstRange.to ? firstRange.to.getTime() : undefined;
    const secondToTimestamp = secondRange.to ? secondRange.to.getTime() : undefined;
    return firstFromTimestamp === secondFromTimestamp && firstToTimestamp === secondToTimestamp;
  };

  const checkPreset = (): void => {
    for (const presetDefinition of PRESETS) {
      const presetRange = getPresetRange(presetDefinition.name);
      if (areRangesEqual(range, presetRange)) {
        setSelectedPreset(presetDefinition.name);
        return;
      }
    }
    setSelectedPreset(undefined);
  };

  const resetValues = (): void => {
    setRange(openedRangeRef.current ?? range);
  };

  useEffect(() => {
    checkPreset();
  }, [range.from?.getTime?.(), range.to?.getTime?.()]);

  const PresetButton: FC<{
    preset: string;
    label: string;
    isSelected: boolean;
  }> = ({ preset, label, isSelected }) => (
    <Button
      variant={isSelected ? 'secondary' : 'ghost'}
      size="custom_sm"
      className={cn('justify-between w-40')}
      data-testid={`drp-preset-${preset}`}
      onClick={() => setPreset(preset)}
    >
      <span>{t(label)}</span>
      {isSelected && <Check className="w-4 h-4" />}
    </Button>
  );

  const renderContent = () => (
    <>
      <div className="flex gap-6 items-start py-2">
        <div className="flex flex-1 min-w-0">
          <div className="flex">
            <div className="flex flex-col gap-1 items-end pr-2 pb-6 pl-2 w-44 shrink-0">
              <div className="flex flex-col gap-1 items-end w-full">
                {PRESETS.map((preset) => (
                  <PresetButton
                    key={preset.name}
                    preset={preset.name}
                    label={preset.label}
                    isSelected={selectedPreset === preset.name}
                  />
                ))}
              </div>
            </div>

            <div>
              <Calendar
                mode="range"
                locale={frLocale}
                onSelect={(selectedRange: { from?: Date; to?: Date } | undefined) => {
                  if (selectedRange?.from) {
                    const from = startOfDay(selectedRange.from);
                    const to = selectedRange.to ? startOfDay(selectedRange.to) : undefined;
                    const nextRange = { from, to: to ?? from } as DateRange;
                    if (nextRange.to) {
                      const months = monthSpan(nextRange.from as Date, nextRange.to as Date);
                      if (months > 23) {
                        setValidationErrorKey('date.range.error.max24months');
                        toast.error(t('date.range.error.max24months'));
                      } else {
                        setValidationErrorKey(undefined);
                      }
                    } else {
                      setValidationErrorKey(undefined);
                    }
                    setRange(nextRange);
                  }
                }}
                selected={range}
                numberOfMonths={2}
                defaultMonth={range.from ?? new Date()}
                data-testid="drp-calendar"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end py-2 pr-4">
        <Button
          rounded="full"
          variant="outline"
          onClick={() => {
            closeActionRef.current = 'reset';
            setIsOpen(false);
            onUpdate?.({ range: { from: undefined, to: undefined } as DateRange });
            onClose?.();
          }}
          data-testid="drp-reset"
        >
          {t('common.reset')}
        </Button>
        <Button
          rounded="full"
          variant="ghost"
          onClick={() => {
            closeActionRef.current = 'cancel';
            setIsOpen(false);
            onClose?.();
          }}
          data-testid="drp-cancel"
        >
          {t('common.back')}
        </Button>
        <Button
          rounded="full"
          onClick={() => {
            if (range.from && range.to && monthSpan(range.from, range.to) > 23) {
              setValidationErrorKey('date.range.error.max24months');
              toast.error(t('date.range.error.max24months'));
              return;
            }
            closeActionRef.current = 'apply';
            setIsOpen(false);
            onUpdate?.({ range });
            onClose?.();
          }}
          data-testid="drp-apply"
        >
          {t('common.save')}
        </Button>
      </div>
    </>
  );

  if (inline) {
    return <div className="w-auto">{renderContent()}</div>;
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (open) {
          openedRangeRef.current = range;
        } else {
          switch (closeActionRef.current) {
            case 'apply':
              break;
            case 'reset':
              setRange({ from: undefined, to: undefined } as DateRange);
              setSelectedPreset(undefined);
              break;
            default:
              resetValues();
              break;
          }
        }
        setIsOpen(open);
        closeActionRef.current = undefined;
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="custom"
          variant="ghost"
          className={cn(
            'gap-3',
            isActive
              ? 'bg-primary text-background hover:bg-primary/90'
              : 'bg-muted hover:bg-muted-foreground/20 text-foreground/50',
            triggerClassName
          )}
          rounded="full"
          data-testid="drp-trigger"
        >
          <div className="text-right">
            <div className="py-1">
              <div>
                {isActive
                  ? (() => {
                      const fromText = range.from ? formatDate(range.from, locale) : '';
                      const toText = range.to ? ` - ${formatDate(range.to, locale)}` : '';
                      return `${fromText}${toText}`;
                    })()
                  : placeholder}
              </div>
            </div>
          </div>
          <div
            className={cn(
              'pl-1 -mr-2 opacity-60 scale-125',
              isActive && 'text-background opacity-80'
            )}
          >
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto rounded-3xl">
        {renderContent()}
      </PopoverContent>
    </Popover>
  );
};
